import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight, Compass, LayoutGrid, ArrowUpRight, RefreshCw } from "lucide-react";

const TOUR_KEY = "vinci.ec.tour.v1";

/**
 * Vinci Executive Command · First-Time Admin Tour.
 *
 * Renders a 4-step overlay on the admin's first landing on /admin/command,
 * once per user (localStorage gated). Each step spotlights a real DOM
 * element via its data-testid, dims the rest of the page, and offers
 * Skip / Back / Next / Enter controls. Vinci obsidian/gold styling.
 */
export function ExecutiveCommandTour({ forceOpen = false, onDone }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (forceOpen) { setVisible(true); setStep(0); return; }
    try {
      if (!localStorage.getItem(TOUR_KEY)) setVisible(true);
    } catch { /* private mode → skip silently */ }
  }, [forceOpen]);

  const STEPS = [
    { testId: "ec-overline",   icon: Compass,
      title: "Welcome to Executive Command.",
      body:  "This is the Vinci admin's mission control — six live windows into every corner of your platform." },
    { testId: "ec-grid",       icon: LayoutGrid,
      title: "Six tiles, one console.",
      body:  "Logistics, Financials, Legal Desk, Wallet Master, Careers, and Live Pulse — every tile has live counts and deep-links straight into its dedicated console." },
    { testId: "ec-tile-legal", icon: ArrowUpRight,
      title: "Deep-links, not drill-downs.",
      body:  "Tap any tile to jump directly to that console tab — no extra navigation needed. Try the Legal Desk tile to see contract lifecycle in one click." },
    { testId: "ec-refresh",    icon: RefreshCw,
      title: "Nothing goes stale.",
      body:  "Stats auto-load on page open. Hit Refresh any time to pull the latest numbers. If a source is unavailable, the tile shows 'Offline' — never a crash." },
  ];

  // Re-measure the spotlight target whenever step changes.
  useEffect(() => {
    if (!visible) return;
    const target = STEPS[step]?.testId
      ? document.querySelector(`[data-testid="${STEPS[step].testId}"]`)
      : null;
    if (!target) { setRect(null); return; }
    // Bring the target into view first so the highlight always shows.
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    // Give the smooth-scroll ~350ms to settle before measuring.
    const t = setTimeout(() => {
      const r = target.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, w: r.width, h: r.height });
    }, 380);
    return () => clearTimeout(t);
  }, [visible, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    try { localStorage.setItem(TOUR_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
    if (onDone) onDone();
  };
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish());
  const back = () => (step > 0 ? setStep(step - 1) : null);
  const skip = () => finish();

  if (!visible) return null;
  const cur = STEPS[step];
  const Icon = cur.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="ec-tour"
        className="fixed inset-0 z-[90] pointer-events-none"
      >
        {/* Dimming scrim with a cut-out around the spotlight rect */}
        <div className="absolute inset-0 pointer-events-auto"
             onClick={skip}
             style={{
               background: rect
                 ? `radial-gradient(ellipse ${Math.max(rect.w, 220)}px ${Math.max(rect.h, 120)}px at ${rect.left + rect.w / 2}px ${rect.top + rect.h / 2}px, transparent 60%, rgba(2,2,2,0.86) 66%)`
                 : "rgba(2,2,2,0.86)",
             }}
        />
        {/* Gold outline around the spotlight rect */}
        {rect && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="pointer-events-none absolute rounded-sm"
            style={{
              top: rect.top - 8, left: rect.left - 8,
              width: rect.w + 16, height: rect.h + 16,
              border: "2px solid #D8B23E",
              boxShadow: "0 0 0 6px rgba(216,178,62,0.18), 0 0 40px rgba(216,178,62,0.35)",
            }}
          />
        )}

        {/* Info card — positioned bottom-center regardless of the target */}
        <motion.div
          key={step}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          data-testid={`ec-tour-step-${step}`}
          className="pointer-events-auto absolute left-1/2 -translate-x-1/2 bottom-8 md:bottom-14 w-[92%] max-w-lg bg-[#0F0C05] border-2 border-[#D8B23E] rounded-sm overflow-hidden"
          style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9), 0 0 40px rgba(216,178,62,0.35)" }}
        >
          <div className="h-1 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]" />
          <div className="p-6 md:p-7">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-full border border-[#D8B23E] bg-[#D8B23E]/10">
                  <Icon className="w-4 h-4 text-[#D8B23E]" />
                </span>
                <div>
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.4em] text-[#8A6A1A]">
                    Vinci Onboarding · Step {step + 1} of {STEPS.length}
                  </div>
                  <h4 className="font-serif text-lg md:text-xl text-[#F5F5F0] mt-1">
                    {cur.title}
                  </h4>
                </div>
              </div>
              <button
                data-testid="ec-tour-skip"
                onClick={skip}
                title="Skip tour"
                className="w-7 h-7 grid place-items-center border border-[#D8B23E]/30 hover:border-[#D8B23E] rounded-full transition-colors"
              >
                <X className="w-3 h-3 text-[#D8B23E]" />
              </button>
            </div>
            <p className="mt-4 text-sm text-[#C9C9C2] leading-relaxed">
              {cur.body}
            </p>
            <div className="mt-5 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {STEPS.map((_, i) => (
                  <span key={i}
                         className="h-1 rounded-full transition-all"
                         style={{
                           width: i === step ? 22 : 8,
                           background: i <= step ? "#D8B23E" : "#3a3a35",
                         }} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                {step > 0 && (
                  <button
                    data-testid="ec-tour-back"
                    onClick={back}
                    className="px-3 py-2 border border-white/15 hover:border-white/30 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9C9C2] transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  data-testid="ec-tour-next"
                  onClick={next}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-[10px] uppercase tracking-[0.28em] hover:shadow-[0_0_28px_rgba(216,178,62,0.55)] transition-shadow"
                >
                  {step === STEPS.length - 1 ? <>Enter Mission Control<Sparkles className="w-3 h-3" /></> : <>Next<ArrowRight className="w-3 h-3" /></>}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
